import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Lock, Key, Smartphone, AlertTriangle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

interface SecuritySettings {
  two_factor_enabled: boolean
  session_timeout: number
  password_expires_days: number
  require_strong_password: boolean
  login_notifications: boolean
  suspicious_activity_alerts: boolean
  last_password_change?: string
  active_sessions?: number
}

export default function SecuritySettings() {
  const navigate = useNavigate()
  const [settings, setSettings] = useState<SecuritySettings>({
    two_factor_enabled: false,
    session_timeout: 60,
    password_expires_days: 90,
    require_strong_password: true,
    login_notifications: true,
    suspicious_activity_alerts: true
  })

  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { user } = useAuth()

  // Check if we're on the demo subdomain
  const isDemoFamily = window.location.hostname.split('.')[0] === 'demo'

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/api/user/security-settings')
      if (response) {
        setSettings(response)
      }
    } catch (error) {
      console.error('Failed to load security settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    setSaving(true)
    try {
      await apiClient.put('/api/user/security-settings', settings)
      toast.success('Security settings saved!')
    } catch (error) {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = (key: keyof SecuritySettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleChange = (key: keyof SecuritySettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handlePasswordChange = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match')
      return
    }
    
    if (passwordData.new.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    
    try {
      await apiClient.post('/api/user/change-password', {
        current_password: passwordData.current,
        new_password: passwordData.new
      })
      toast.success('Password changed successfully!')
      setShowPasswordForm(false)
      setPasswordData({ current: '', new: '', confirm: '' })
    } catch (error) {
      toast.error('Failed to change password')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Account Security */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Lock className="w-5 h-5 mr-2 text-blue-500" />
          Account Security
        </h3>
        
        <div className="space-y-4">
          {/* Password Section - Hidden for demo family */}
          {!isDemoFamily && (
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium">Password</div>
                  <div className="text-sm text-gray-500">
                    {settings.last_password_change
                      ? `Last changed: ${new Date(settings.last_password_change).toLocaleDateString()}`
                      : 'Never changed'}
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  Change Password
                </button>
              </div>

              {showPasswordForm && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                        className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.new}
                        onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                        className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirm}
                        onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                        className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={handlePasswordChange}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                      Update Password
                    </button>
                    <button
                      onClick={() => setShowPasswordForm(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Two-Factor Authentication - Hidden for demo family */}
          {!isDemoFamily && (
            <label className="flex items-center justify-between">
              <div className="flex items-center">
                <Smartphone className="w-5 h-5 mr-3 text-gray-400" />
                <div>
                  <div className="font-medium">Two-Factor Authentication</div>
                  <div className="text-sm text-gray-500">Extra security with phone verification</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={settings.two_factor_enabled}
                onChange={() => handleToggle('two_factor_enabled')}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>
          )}

          {/* Demo family notice */}
          {isDemoFamily && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <Shield className="w-5 h-5 mr-3 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-blue-900">Demo Family Account</div>
                  <div className="text-sm text-blue-700 mt-1">
                    Password and two-factor authentication settings are not available for demo accounts.
                    These accounts are reset nightly for demonstration purposes.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Key className="w-5 h-5 mr-2 text-purple-500" />
          Session Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (minutes)
            </label>
            <select
              value={settings.session_timeout}
              onChange={(e) => handleChange('session_timeout', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={480}>8 hours</option>
              <option value={1440}>24 hours</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Automatically log out after this period of inactivity
            </p>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">Active Sessions:</span> {settings.active_sessions || 'View'}
              </div>
              <button
                onClick={() => navigate('/dashboard/sessions')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All Sessions →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Security Alerts */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
          Security Alerts
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Login Notifications</div>
                <div className="text-sm text-gray-500">Alert when account is accessed</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.login_notifications}
              onChange={() => handleToggle('login_notifications')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Suspicious Activity Alerts</div>
                <div className="text-sm text-gray-500">Notify of unusual account activity</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.suspicious_activity_alerts}
              onChange={() => handleToggle('suspicious_activity_alerts')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      {/* Password Policy */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-green-500" />
          Password Policy
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Lock className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Require Strong Passwords</div>
                <div className="text-sm text-gray-500">Minimum 8 characters with mixed case and numbers</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.require_strong_password}
              onChange={() => handleToggle('require_strong_password')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password Expiration (days)
            </label>
            <select
              value={settings.password_expires_days}
              onChange={(e) => handleChange('password_expires_days', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={0}>Never</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  )
}